import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Timer from "tiny-timer";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import { TldrAnimationPlaybackAction } from "../../../common/statelessView";
import {
  StyledAdvEditorToolbarFormatGroup,
  StyledAdvEditorToolbarRow,
} from "../../../styled/details/stylesDetails";
import DurationIndicator from "../../../../TldrStoryDetail/TimelineEditor/DurationIndicator";
import {
  DEFAULT_STORY_MUSIC_ELEMENT_ID,
  DOMAIN_1P5_TO_30,
  FABRIC_VIDEO_ELEMENT,
} from "../../constants";
import {
  wsUpdateAnimation,
  wsUpdateLayer,
} from "../../../../_actions/webSocketAction";
import { isEmpty, isEqual } from "lodash";
import "react-circular-progressbar/dist/styles.css";
import { STILL_ANIMATION } from "./defaultSlideTransitions";
import TldrRndSlider from "../../../common/TldrRndSlider";
import { DEFAULT_ARTBOARD_DURATION } from "../../../../_utils/constants";
import { roundToNearest } from "../../../../_utils/common";
import { FaPause, FaPlay, FaStop } from "react-icons/fa";
import { withTranslation } from "react-i18next";
import TldrChakraSlider from "../../../common/TldrChakraSlider";
import { setCurrentPlayingAnimation } from "../../../../_actions/animationActions";

export class StudioAnimationPlayer extends Component {
  constructor(props) {
    super(props);
    this.countdownTimer = new Timer({ interval: 10 });
    this.adds = [];
    this.audioOffset = 0;
  }

  state = {
    timeout: 0,
    seekTime: 0,
    offset: 0,
    minTime: 0,
    maxTime: null,
    startTime: 0,
    endTime: 0,
  };

  componentDidMount() {
    const { storyMusic, layers } = this.props;
    this.initializeStoryAudio(storyMusic);

    this.countdownTimer.on("tick", this.onTimerTick);
    this.countdownTimer.on("statusChanged", this.onTimerStatusChanged);
    const currentLayer = layers[this.props.editor?.activeElement?.id];

    const currentAnimations = currentLayer?.payload?.animation;
    if (currentLayer) {
      this.setStartAndEndTime(currentAnimations, currentLayer);
    }
  }

  onTimerTick = (ms) => {
    this.adds.push(ms);
    this.setState({ timeout: ms });
  };

  onTimerStatusChanged = (status) => {
    const { canvasRef } = this.props;
    if (status === "stopped") {
      const diffs = [];
      for (let i = 0; i < this.adds.length - 1; i++) {
        diffs.push(this.adds[i] - this.adds[i + 1]);
      }

      this.setState({ timeout: 0 });
      canvasRef.handler?.audioHandler?.stopAll();
    }
  };

  setStartAndEndTime = (animations, layerData) => {
    let startTime = 0;
    let endTime = 0;
    let minAnimationTime = 500;
    if (animations && animations.length) {
      startTime = animations[0].start_time;
      let duration = 0;
      animations.forEach((a) => {
        duration += a.trans_duration;
        if (a.type !== STILL_ANIMATION.id) {
          minAnimationTime += a.trans_duration;
        }
      });
      endTime = startTime + duration;
    }

    let maxTime = null;

    if (layerData.mime === "video") {
      maxTime =
        (layerData?.payload?.duration ?? 0) -
        (layerData?.payload?.startTime ?? 0);
    }

    this.setState({
      startTime: Number((startTime / 1000).toFixed(1)),
      endTime: Number((endTime / 1000).toFixed(1)),
      minTime: Number((minAnimationTime / 1000).toFixed(1)),
      maxTime: maxTime,
    });
  };

  get currentPageDuration() {
    const { editor, pages } = this.props;
    const currentPage = pages[editor.activePage.id];
    return (
      currentPage.payload?.animation?.duration ?? DEFAULT_ARTBOARD_DURATION
    );
  }

  componentDidUpdate(prevProps, prevState) {
    const currentLayer =
      this.props.layers[this.props.editor?.activeElement?.id];
    const previousLayer = prevProps.layers[prevProps.editor?.activeElement?.id];
    const currentAnimations = currentLayer?.payload?.animation;
    const previousAnimations = previousLayer?.payload?.animation;

    if (prevProps.storyMusic !== this.props.storyMusic) {
      if (!this.props.storyMusic) {
        this.initializeStoryAudio(null);
      } else if (prevProps.storyMusic?.src !== this.props?.storyMusic?.src) {
        this.initializeStoryAudio(this.props.storyMusic);
      }
    }

    if (
      prevProps.editor?.activePage?.id !== this.props.editor?.activePage?.id
    ) {
      this.audioOffset = this.getCurrentArtboardAudioOffset();
      this.props.canvasRef.handler?.audioHandler?.stopAll();
      this.props.canvasRef.handler?.audioHandler?.seek(
        DEFAULT_STORY_MUSIC_ELEMENT_ID,
        this.audioOffset
      );
    }
    if (
      currentLayer &&
      (!isEqual(previousAnimations, currentAnimations) ||
        previousLayer?.payload?.startTime !==
          currentLayer?.payload?.startTime ||
        previousLayer?.payload?.endTime !== currentLayer?.payload?.endTime)
    ) {
      this.setStartAndEndTime(currentAnimations, currentLayer);
    }
  }

  componentWillUnmount() {
    const { canvasRef } = this.props;
    canvasRef.handler?.audioHandler?.clearAllMusic();
    canvasRef.handler?.animationHandler?.stopAll();
    this.countdownTimer.off("tick", this.onTimerTick);
    this.countdownTimer.off("statusChanged", this.onTimerStatusChanged);
  }

  initializeStoryAudio = (audioSource = null) => {
    const { canvasRef } = this.props;
    if (!audioSource) {
      canvasRef.handler?.audioHandler?.initialize([]); // Clear and stop existing music
    } else {
      this.audioOffset = this.getCurrentArtboardAudioOffset();
      canvasRef.handler?.audioHandler?.initialize([
        {
          id: DEFAULT_STORY_MUSIC_ELEMENT_ID,
          src: audioSource.src,
          offset: this.audioOffset,
          volume: audioSource.volume,
        },
      ]);
    }
  };

  getCurrentArtboardAudioOffset = () => {
    const {
      editor: { activePage: { id: activePageId } = {} },
      pages,
      storyMusic,
    } = this.props;

    let startOffset = 0;

    if (storyMusic && storyMusic.start) {
      startOffset = storyMusic.start;
    }

    const sortedPages = Object.keys(pages)
      .map((pIdx) => pages[pIdx])
      .sort((a, b) => (a.order > b.order ? 1 : -1));
    const activePageIndex = sortedPages.findIndex((o) => o.id === activePageId);
    if (activePageIndex === 0) {
      return startOffset;
    } else {
      const previousPages = sortedPages.slice(0, activePageIndex);
      return (
        previousPages.reduce((sum, obj) => {
          const animationDuration = obj?.payload?.animation?.duration ?? 6;
          return sum + animationDuration;
        }, 0) + startOffset
      );
    }
  };

  playerAction = async (action) => {
    const {
      editor,
      layers,
      pages,
      canvasRef,
      currentTab,
      setCurrentPlayingAnimation,
    } = this.props;
    if (action === "stop") {
      canvasRef.handler?.animationHandler?.stopAll();
      setCurrentPlayingAnimation(null);
      this.countdownTimer.stop();
      canvasRef.handler?.audioHandler?.stop(DEFAULT_STORY_MUSIC_ELEMENT_ID);
      this.props.canvasRef.handler?.audioHandler?.seek(
        DEFAULT_STORY_MUSIC_ELEMENT_ID,
        this.audioOffset
      );
      setCurrentPlayingAnimation(null);
      return;
    } else if (action === "pause") {
      canvasRef.handler?.animationHandler?.pauseAll();
      this.countdownTimer.pause();
      canvasRef.handler?.audioHandler?.pause(DEFAULT_STORY_MUSIC_ELEMENT_ID);
      return;
    }

    if (editor.activeElement && editor.activeElement.id) {
      const currentAnimation = pages[editor.activePage.id].payload.animation;
      const currentAnimations =
        layers[editor.activeElement.id].payload.animation;

      if (action === "play" && currentAnimations) {
        if (this.countdownTimer.time) {
          this.countdownTimer.resume();
        } else {
          this.countdownTimer.start(currentAnimation.duration * 1000);
        }
        setCurrentPlayingAnimation(
          currentAnimations.map((item) => item.parentId)
        );
        await canvasRef.handler?.animationHandler?.previewAndUpdate(
          editor.activeElement,
          currentAnimations,
          -1,
          false,
          currentAnimation.duration
        );
        setCurrentPlayingAnimation(null);
      }
    } else {
      const currentAnimation = pages[editor.activePage.id].payload.animation;
      if (this.countdownTimer.time) {
        this.countdownTimer.resume();
      } else {
        this.countdownTimer.start(currentAnimation.duration * 1000);
      }

      canvasRef.handler?.audioHandler?.play(DEFAULT_STORY_MUSIC_ELEMENT_ID);
      setCurrentPlayingAnimation(["global"]);
      await canvasRef.handler?.animationHandler?.previewCurrentArtboard(
        currentAnimation.duration
      );
      setCurrentPlayingAnimation(null);
    }
  };

  updateClipDuration = (newDuration) => {
    const { editor, pages } = this.props;

    let duration = Number(newDuration[0].toFixed(1));
    const currentPage = pages[editor.activePage.id];
    const objectPayload = currentPage.payload;
    if (objectPayload["animation"]) {
      if (objectPayload["animation"]["duration"] === duration) {
        return;
      }
      objectPayload["animation"]["duration"] = duration;
    } else {
      objectPayload["animation"] = { duration: duration };
    }
    let message = {
      page: currentPage.id,
      payload: objectPayload,
    };
    this.props.wsUpdateAnimation(message);
  };

  setupStillDuration = (startTime, endTime, still) => {
    const { editor, pages } = this.props;

    if (editor.activeElement.mime === FABRIC_VIDEO_ELEMENT) {
      const currentAnimation = pages[editor.activePage.id].payload.animation;
      let defaultDuration = currentAnimation["duration"] * 1000;

      let videoDuration =
        (editor.activeElement.format.endTime -
          editor.activeElement.format.startTime) *
        1000;
      if (still["start_time"] !== startTime) {
        still["start_time"] = startTime;
        still["end_time"] =
          startTime + Math.min(defaultDuration, startTime + videoDuration);
      } else if (still["end_time"] !== endTime) {
        still["end_time"] = endTime;
        still["start_time"] = Math.max(startTime, endTime - videoDuration);
      }
    } else {
      still["start_time"] = startTime;
      still["end_time"] = endTime;
    }
    still["trans_duration"] = still["end_time"] - still["start_time"];
  };

  updateLayerDuration = (newDuration) => {
    const { editor, layers, pages } = this.props;

    let startTime = Number(newDuration[0].toFixed(1)) * 1000;
    let endTime = Number(newDuration[1].toFixed(1)) * 1000;
    let totalDuration = endTime - startTime;

    const currentLayerPayload = layers[editor.activeElement.id].payload;
    let currentLayerAnimations = currentLayerPayload
      ? currentLayerPayload.animation
      : [];
    let enterAnimation, still, exitAnimation;

    if (isEmpty(currentLayerAnimations)) {
      currentLayerAnimations = [];
    }

    switch (currentLayerAnimations.length) {
      case 0:
        still = STILL_ANIMATION.variants[0].enter;
        this.setupStillDuration(startTime, endTime, still);
        this.props.canvasRef.handler?.set("animation", [still]);
        break;
      case 1:
        still = currentLayerAnimations[0];
        this.setupStillDuration(startTime, endTime, still);
        this.props.canvasRef.handler?.set("animation", [still]);
        break;
      case 2:
        // Enter animation + still
        enterAnimation = currentLayerAnimations[0];
        still = currentLayerAnimations[1];

        if (editor.activeElement.mime === FABRIC_VIDEO_ELEMENT) {
          if (enterAnimation["start_time"] !== startTime) {
            enterAnimation["start_time"] = startTime;
            enterAnimation["end_time"] =
              startTime + enterAnimation["trans_duration"];
            still["start_time"] = enterAnimation["end_time"];

            let availableMaxDuration =
              totalDuration - enterAnimation["trans_duration"];
            let videoMaxDuration =
              (editor.activeElement.format.endTime -
                editor.activeElement.format.startTime) *
                1000 -
              enterAnimation["trans_duration"];
            still["trans_duration"] = Math.min(
              availableMaxDuration,
              videoMaxDuration
            );

            still["end_time"] = still["start_time"] + still["trans_duration"];
          } else if (still["end_time"] !== endTime) {
            still["end_time"] = endTime;
            let availableMaxDuration = totalDuration - still["trans_duration"];
            let videoMaxDuration =
              (editor.activeElement.format.endTime -
                editor.activeElement.format.startTime) *
                1000 -
              enterAnimation["trans_duration"];
            still["trans_duration"] = Math.min(
              availableMaxDuration,
              videoMaxDuration
            );
            enterAnimation["end_time"] = still["start_time"] =
              still["end_time"] - still["trans_duration"];
            enterAnimation["start_time"] =
              enterAnimation["end_time"] - enterAnimation["trans_duration"];
          }
        } else if (
          enterAnimation["start_time"] !== startTime ||
          still["end_time"] !== endTime
        ) {
          enterAnimation["start_time"] = startTime;
          enterAnimation["end_time"] =
            startTime + enterAnimation["trans_duration"];
          still["start_time"] = enterAnimation["end_time"];
          still["trans_duration"] =
            totalDuration - enterAnimation["trans_duration"];
          still["end_time"] = still["start_time"] + still["trans_duration"];
        }

        this.props.canvasRef.handler?.set("animation", [enterAnimation, still]);
        break;
      case 3:
        enterAnimation = currentLayerAnimations[0];
        exitAnimation = currentLayerAnimations[2];

        if (
          enterAnimation["start_time"] !== startTime ||
          exitAnimation["end_time"] !== endTime
        ) {
          enterAnimation["start_time"] = startTime;
          enterAnimation["end_time"] =
            startTime + enterAnimation["trans_duration"];

          still = currentLayerAnimations[1];
          still["start_time"] = enterAnimation["end_time"];
          still["trans_duration"] =
            totalDuration -
            exitAnimation["trans_duration"] -
            exitAnimation["trans_duration"];
          still["end_time"] = still["start_time"] + still["trans_duration"];

          exitAnimation["start_time"] = still["end_time"];
          exitAnimation["end_time"] =
            exitAnimation["start_time"] + exitAnimation["trans_duration"];

          this.props.canvasRef.handler?.set("animation", [
            enterAnimation,
            still,
            exitAnimation,
          ]);
        }
        break;
      default:
    }
  };

  onChangeDuration = (newDuration) => {
    const { editor } = this.props;
    if (editor.activeElement && editor.activeElement.id) {
      this.updateLayerDuration(newDuration);
    } else {
      this.updateClipDuration(newDuration);
    }
  };

  onChangeInputClipDuration = (newDuration) => {
    this.updateClipDuration([newDuration]);
  };

  onChangeSeekTime = (newDuration) => {
    const { pages, canvasRef, editor } = this.props;
    const currentAnimation = pages[editor.activePage.id].payload.animation;
    let point = Number(newDuration[0].toFixed(1));
    this.setState({ seekTime: point });
    canvasRef.handler?.animationHandler?.seekToPosition(
      point,
      currentAnimation.duration
    );
  };

  onChangeOnScreenDuration = (start, end) => {
    const { minTime, startTime, endTime } = this.state;
    const { editor, layers } = this.props;
    const currentLayer = layers[editor?.activeElement?.id];

    const newDuration = end - start;
    let oldDuration = endTime - startTime;

    if (newDuration >= minTime) {
      const currentAnimations = currentLayer?.payload?.animation;
      let animationDuration = 0;
      if (currentAnimations && currentAnimations.length) {
        currentAnimations.forEach((a) => {
          if (a.type !== STILL_ANIMATION.id) {
            animationDuration += a.trans_duration;
          }
        });
      }

      const offsetToAdjustInStillDuration = (newDuration - oldDuration) * 1000;
      let prevOffset = start * 1000;
      const newAnimations = currentAnimations?.map((a) => {
        const animationStart = roundToNearest(prevOffset, 100);
        let transDuration = a.trans_duration;

        if (a.type === STILL_ANIMATION.id) {
          if (offsetToAdjustInStillDuration < 0) {
            // if(Math.abs(offsetToAdjustInStillDuration) > a.trans_duration){

            // }else{
            transDuration = roundToNearest(
              a.trans_duration + offsetToAdjustInStillDuration,
              100
            );

            // }
          } else {
            transDuration = roundToNearest(
              a.trans_duration + offsetToAdjustInStillDuration,
              100
            );
          }
        }

        prevOffset = roundToNearest(animationStart + transDuration, 100);
        return {
          ...a,
          start_time: animationStart,
          trans_duration: transDuration,
          end_time: prevOffset,
        };
      });

      this.props.canvasRef.handler?.set(
        "endTime",
        (currentLayer?.payload?.startTime ?? 0) + newDuration
      );
      this.props.canvasRef.handler?.set("animation", newAnimations);
    }
  };

  render() {
    const { startTime, endTime, minTime, maxTime } = this.state;
    const { editor, layers, pages, t } = this.props;
    const { activeElement, activePage } = editor;
    const { id: activePageId } = activePage;
    const { id: activeElementId } = activeElement;
    if (!activePageId) {
      return <></>;
    }
    let showPreview = true,
      defaultMaxClipDuration = DOMAIN_1P5_TO_30,
      step = 0.1;

    const currentAnimation = pages[activePageId]?.payload?.animation;
    let defaultDuration = currentAnimation["duration"];
    let clipDuration = [defaultDuration];
    let seekTime = [this.state.seekTime];
    let elementDuration = 0;

    if (editor.activeElement && activeElementId) {
      defaultMaxClipDuration = [0, defaultDuration];
      let totalTime = defaultDuration;

      showPreview = layers[activeElementId]?.payload.animation;
      const currentAnimations = layers[activeElementId]?.payload.animation;
      let startTime = 0;

      if (currentAnimations) {
        startTime = currentAnimations[0]?.start_time / 1000;

        if (currentAnimations.length === 2) {
          totalTime = currentAnimations[1]?.end_time / 1000;
        } else if (currentAnimations.length === 3) {
          totalTime = currentAnimations[2]?.end_time / 1000;
        } else {
          totalTime = currentAnimations[0]?.end_time / 1000;
        }
        elementDuration =
          currentAnimations.reduce((sum, item) => {
            return sum + item.trans_duration;
          }, 0) / 1000;
      } else if (layers[activeElementId]?.mime === FABRIC_VIDEO_ELEMENT) {
        totalTime = Math.min(
          totalTime,
          layers[activeElementId]?.payload?.endTime
        );
      }
      clipDuration = [startTime, totalTime];
    }
    const { timeout } = this.state;

    return (
      <>
        <StyledAdvEditorToolbarRow direction="column" margin="0px">
          <StyledAdvEditorToolbarFormatGroup
            mb="1rem"
            style={{ flex: "1 1 0%" }}
          >
            <div className="title">
              {activeElementId ? t("preview") : t("preview_all")}
            </div>
            <div className="actions">
              {/* {activeElementId ? (
                <TldrAnimationPlaybackAction
                  action="play"
                  icon="play"
                  title="Play"
                  callback={this.playerAction}
                  disabled={showPreview ? false : true}
                  margin="0 0.2rem"
                />
              ) : ( */}
              <div style={{ width: 40, height: 40, margin: "0 0.2rem" }}>
                <CircularProgressbarWithChildren
                  value={
                    timeout
                      ? Math.floor(
                          (100 * (defaultDuration * 1000 - timeout)) /
                            (defaultDuration * 1000)
                        )
                      : 0
                  }
                  strokeWidth={6}
                  styles={buildStyles({
                    pathColor: "#ffac41",
                  })}
                >
                  <TldrAnimationPlaybackAction
                    action="play"
                    icon={<FaPlay />}
                    title={t("play")}
                    callback={this.playerAction}
                    disabled={showPreview ? false : true}
                  />
                </CircularProgressbarWithChildren>
              </div>
              {/* )} */}

              <TldrAnimationPlaybackAction
                action="pause"
                icon={<FaPause />}
                title={t("pause")}
                callback={this.playerAction}
                disabled={showPreview ? false : true}
                margin="0 0.2rem"
              />

              <TldrAnimationPlaybackAction
                action="stop"
                icon={<FaStop />}
                title={t("stop")}
                callback={this.playerAction}
                disabled={showPreview ? false : true}
                margin="0 0.2rem"
              />
              <div>
                <DurationIndicator
                  current={
                    (timeout ? defaultDuration * 1000 - timeout : 0) / 1000
                  }
                  total={defaultDuration}
                />
              </div>
            </div>
          </StyledAdvEditorToolbarFormatGroup>
          <StyledAdvEditorToolbarFormatGroup
            style={{
              flex: "1 1 0%",
              paddingLeft: "0.25rem",
              paddingRight: "0.25rem",
            }}
          >
            {activeElementId ? (
              <>
                <label className="title">{t("on_scree_dur")}</label>
                <TldrRndSlider
                  onChange={this.onChangeOnScreenDuration}
                  totalValue={defaultDuration}
                  start={startTime}
                  end={endTime}
                  minValue={minTime}
                  maxValue={maxTime}
                  showInputBoxes
                />
              </>
            ) : (
              <TldrChakraSlider
                // disabled={showPreview ? false : true}
                classNames="w-83"
                domain={defaultMaxClipDuration}
                values={clipDuration[0]}
                step={step}
                onUpdate={(value) => this.onChangeDuration([value])}
                showDecimals={true}
                title={activeElementId ? t("on_screen") : t("duration_sec")}
                maxLength={5}
                handleFocusOut={this.onChangeInputClipDuration}
                showInputbox={true}
                variant="solid"
              />
            )}
          </StyledAdvEditorToolbarFormatGroup>
        </StyledAdvEditorToolbarRow>
      </>
    );
  }
}

StudioAnimationPlayer.propTypes = {
  editor: PropTypes.object.isRequired,
  currentTab: PropTypes.string,
};

const mapStateToProps = (state) => ({
  editor: state.editor,
  layers: state.layerstore.layers,
  pages: state.pagestore.pages,
  storyMusic: state.story?.payload?.payload?.music,
});

const mapDispatchToProps = (dispatch) => ({
  wsUpdateAnimation: (payload) => dispatch(wsUpdateAnimation(payload)),
  wsUpdateLayer: (payload) => dispatch(wsUpdateLayer(payload)),
  setCurrentPlayingAnimation: (payload) =>
    dispatch(setCurrentPlayingAnimation(payload)),
});

export default withTranslation(["common"])(
  connect(mapStateToProps, mapDispatchToProps)(StudioAnimationPlayer)
);
